import React, { useEffect, useState } from 'react';
import { IngredientMatchingService } from '../../services/ingredientMatchingService';
import { MatchedIngredient, StandardIngredient, IngredientCategory } from '../../types/ingredients';
import { TikTokRecipe } from '../../types/tiktok';
import { Check, X, Search, ArrowRight, Plus } from 'lucide-react';
import { getUnitLabel, getCategoryLabel } from '../../utils/translationHelpers';
import { AddAliasModal } from './AddAliasModal';
import { CreateIngredientModal } from './CreateIngredientModal';
import { doc, updateDoc, arrayUnion, addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { StandardUnit } from '../../types/units';

interface TikTokIngredientMatcherProps {
  recipe: TikTokRecipe;
  onConfirm: (matches: MatchedIngredient[]) => void;
  onSkip: () => void;
}

export const TikTokIngredientMatcher: React.FC<TikTokIngredientMatcherProps> = ({ 
  recipe, 
  onConfirm,
  onSkip 
}) => {
  const [matches, setMatches] = useState<MatchedIngredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StandardIngredient[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [service] = useState(() => new IngredientMatchingService());
  
  // Modal states
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingMatch, setPendingMatch] = useState<{
    index: number;
    ingredient: StandardIngredient;
    extractedName: string;
  } | null>(null);
  const [createIngredientIndex, setCreateIngredientIndex] = useState<number | null>(null);

  useEffect(() => {
    initializeMatching();
  }, [recipe]);

  const initializeMatching = async () => {
    setLoading(true);
    try {
      await service.initialize();
      const matchedResults = service.matchIngredients(recipe.ingredients);
      setMatches(matchedResults);
    } catch (error) {
      console.error('Error initializing matching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string, index: number) => {
    setSearchQuery(query);
    setSelectedIndex(index);
    if (query.trim()) {
      const results = service.searchIngredients(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectMatch = (index: number, ingredient: StandardIngredient) => {
    const extractedName = matches[index].extracted.name;
    
    // Check if the extracted name is already in aliases or exact match
    const normalizedExtracted = extractedName.toLowerCase().trim();
    const normalizedIngredientName = ingredient.name.toLowerCase().trim();
    const isExactMatch = normalizedExtracted === normalizedIngredientName;
    const isInAliases = ingredient.aliases?.some(
      alias => alias.toLowerCase().trim() === normalizedExtracted
    );

    // If manual selection and not exact match, propose to add as alias
    if (!isExactMatch && !isInAliases) {
      setPendingMatch({ index, ingredient, extractedName });
      setShowAliasModal(true);
    } else {
      // Direct match without modal
      applyMatch(index, ingredient);
    }
  };

  const applyMatch = (index: number, ingredient: StandardIngredient) => {
    const newMatches = [...matches];
    newMatches[index] = {
      ...newMatches[index],
      matched: ingredient,
      confidence: 'high'
    };
    setMatches(newMatches);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedIndex(null);
  };

  const handleRemoveMatch = (index: number) => {
    const newMatches = [...matches];
    newMatches[index] = {
      ...newMatches[index],
      matched: undefined,
      confidence: 'none'
    };
    setMatches(newMatches);
  };

  const handleConfirmAlias = async () => {
    if (!pendingMatch) return;

    try {
      // Update ingredient in Firestore
      const ingredientRef = doc(db, 'standardIngredients', pendingMatch.ingredient.id);
      await updateDoc(ingredientRef, {
        aliases: arrayUnion(pendingMatch.extractedName)
      });

      // Reload service to get updated data
      await service.reload();

      // Apply the match
      applyMatch(pendingMatch.index, {
        ...pendingMatch.ingredient,
        aliases: [...(pendingMatch.ingredient.aliases || []), pendingMatch.extractedName]
      });

      setShowAliasModal(false);
      setPendingMatch(null);
    } catch (error) {
      console.error('Error adding alias:', error);
      alert('Erreur lors de l\'ajout de l\'alias. Veuillez réessayer.');
    }
  };

  const handleSkipAlias = () => {
    if (!pendingMatch) return;
    
    // Apply match without adding alias
    applyMatch(pendingMatch.index, pendingMatch.ingredient);
    setShowAliasModal(false);
    setPendingMatch(null);
  };

  const handleCreateIngredient = async (data: {
    name: string;
    category: IngredientCategory;
    preferredUnit: StandardUnit;
  }) => {
    if (createIngredientIndex === null) return;

    try {
      // Get browser language
      const browserLanguage = navigator.language.split('-')[0].toLowerCase() as 'en' | 'fr';

      // Create new ingredient in Firestore
      const newIngredient = {
        name: data.name,
        language: browserLanguage,
        category: data.category,
        aliases: [],
        preferredUnit: data.preferredUnit,
        picture: '',
        attributes: {
          glutenFree: false,
          vegan: false,
          vegetarian: false,
          organic: false,
          allergens: [],
          seasonal: []
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'standardIngredients'), newIngredient);

      // Reload service with new ingredient
      await service.reload();

      // Apply the match with the newly created ingredient
      const createdIngredient: StandardIngredient = {
        ...newIngredient,
        id: docRef.id
      };

      applyMatch(createIngredientIndex, createdIngredient);

      setShowCreateModal(false);
      setCreateIngredientIndex(null);
    } catch (error) {
      console.error('Error creating ingredient:', error);
      alert('Erreur lors de la création de l\'ingrédient. Veuillez réessayer.');
    }
  };

  const handleOpenCreateModal = (index: number) => {
    setCreateIngredientIndex(index);
    setShowCreateModal(true);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedIndex(null);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'bg-green-50 border-green-300 text-green-800';
      case 'medium': return 'bg-yellow-50 border-yellow-300 text-yellow-800';
      case 'low': return 'bg-orange-50 border-orange-300 text-orange-800';
      default: return 'bg-gray-50 border-gray-300 text-gray-600';
    }
  };

  const getConfidenceLabel = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'Haute confiance';
      case 'medium': return 'Confiance moyenne';
      case 'low': return 'Faible confiance';
      default: return 'Aucune correspondance';
    }
  };

  const matchedCount = matches.filter(m => m.matched).length;
  const totalCount = matches.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Chargement des ingrédients standards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            🔍 Correspondance des ingrédients
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Vérifiez et ajustez les correspondances avec la base de données Menioo
          </p>
        </div>
        <div className="text-sm font-medium text-gray-700 px-3 py-1 bg-gray-100 rounded-full">
          {matchedCount} / {totalCount} ingrédients matchés
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {matches.map((match, index) => (
          <div key={index} className={`p-4 border-2 rounded-lg ${getConfidenceColor(match.confidence)}`}>
            {/* Ingredient extrait */}
              <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{match.extracted.name}</span>
                <span className="text-sm text-gray-600">
                  {match.extracted.amount > 0 && `${match.extracted.amount} ${getUnitLabel(match.extracted.unit)}`}
                </span>
              </div>
              
              {/* Search box */}
              {selectedIndex === index && (
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value, index)}
                    placeholder="Rechercher un ingrédient..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => handleSelectMatch(index, result)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium text-gray-900">{result.name}</span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{getCategoryLabel(result.category)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Match result or search button */}
              {match.matched ? (
                <div className="flex items-start justify-between bg-white bg-opacity-50 p-3 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ArrowRight className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-gray-900">{match.matched.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 text-xs">
                      <span className="bg-white px-2 py-0.5 rounded border border-gray-200">
                        {getCategoryLabel(match.matched.category)}
                      </span>
                      <span className="bg-white px-2 py-0.5 rounded border border-gray-200">
                        {getUnitLabel(match.matched.preferredUnit)}
                      </span>
                      <span className="px-2 py-0.5 rounded border border-gray-200">
                        {getConfidenceLabel(match.confidence)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMatch(index)}
                    className="ml-2 p-1 hover:bg-red-100 rounded transition-colors"
                    title="Supprimer la correspondance"
                  >
                    <X className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setSelectedIndex(index);
                      handleSearch(match.extracted.name, index);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Search className="h-4 w-4" />
                    <span className="text-sm font-medium">Rechercher une correspondance</span>
                  </button>
                  <button
                    onClick={() => handleOpenCreateModal(index)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-50 border border-green-300 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm font-medium">Créer un nouvel ingrédient</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <button
          onClick={onSkip}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Passer cette étape
        </button>
        <button
          onClick={() => onConfirm(matches)}
          disabled={matchedCount === 0}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="h-5 w-5" />
          Confirmer ({matchedCount} matches)
        </button>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Astuce :</strong> Les correspondances automatiques sont basées sur les ingrédients standards de Menioo. 
          Utilisez la recherche pour trouver et sélectionner manuellement les correspondances.
        </p>
      </div>

      {/* Modals */}
      {pendingMatch && (
        <AddAliasModal
          isOpen={showAliasModal}
          onClose={handleSkipAlias}
          onConfirm={handleConfirmAlias}
          extractedName={pendingMatch.extractedName}
          selectedIngredient={pendingMatch.ingredient}
        />
      )}

      {createIngredientIndex !== null && (
        <CreateIngredientModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setCreateIngredientIndex(null);
          }}
          onConfirm={handleCreateIngredient}
          extractedName={matches[createIngredientIndex].extracted.name}
          extractedUnit={matches[createIngredientIndex].extracted.unit}
        />
      )}
    </div>
  );
};
